import { useState } from "react";
import Layout from "@/components/Layout";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COMPANY, COUNTRIES, SERVICE_INTERESTS } from "@/data/content";
import { Phone, Mail, MapPin, Send, MessageCircle, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { motion } from "framer-motion";

export default function Contact() {
  const [form, setForm] = useState({
    full_name: "", company: "", email: "", phone: "",
    country: "", message: "", service_interest: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", form);
      setSuccess(true);
      toast.success("Thanks! We'll be in touch within 24 hours.");
      setForm({ full_name:"", company:"", email:"", phone:"", country:"", message:"", service_interest:"" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally { setSubmitting(false); }
  };

  return (
    <Layout>
      <section className="relative pt-16 pb-20">
        <div className="absolute inset-0 hero-glow opacity-60" aria-hidden />
        <div className="absolute inset-0 grid-bg opacity-30" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline">Contact</div>
            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05]" data-testid="contact-title">
              Ready to launch<br/>your <span className="text-gradient-blue">iGaming brand?</span>
            </h1>
            <p className="mt-5 text-gray-400 leading-relaxed max-w-md">
              Drop your details and our solutions team will reach out with a tailored proposal.
            </p>

            <div className="mt-10 grid gap-4">
              <div className="p-5 rounded-2xl glass flex items-start gap-4" data-testid="contact-info-office">
                <div className="w-10 h-10 rounded-lg bg-[#2563EB]/20 border border-[#2563EB]/40 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#00D4FF]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Office</div>
                  <div className="mt-1 text-sm">{COMPANY.address}</div>
                </div>
              </div>
              <div className="p-5 rounded-2xl glass flex items-start gap-4" data-testid="contact-info-phone">
                <div className="w-10 h-10 rounded-lg bg-[#2563EB]/20 border border-[#2563EB]/40 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-[#00D4FF]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Phone</div>
                  <a href={`tel:${COMPANY.phone}`} className="mt-1 text-sm hover:text-white">{COMPANY.phone}</a>
                </div>
              </div>
              <div className="p-5 rounded-2xl glass flex items-start gap-4" data-testid="contact-info-email">
                <div className="w-10 h-10 rounded-lg bg-[#2563EB]/20 border border-[#2563EB]/40 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#00D4FF]" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Email</div>
                  <a href={`mailto:${COMPANY.email}`} className="mt-1 text-sm hover:text-white">{COMPANY.email}</a>
                </div>
              </div>
              <a
                href="https://wa.me/447577308890"
                target="_blank"
                rel="noreferrer"
                data-testid="contact-whatsapp-link"
                className="p-5 rounded-2xl glass flex items-center gap-4 hover:bg-[#0B1224]"
              >
                <div className="w-10 h-10 rounded-lg bg-[#25D366]/20 border border-[#25D366]/40 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-[#25D366]"/>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-gray-500">WhatsApp</div>
                  <div className="mt-1 text-sm">Chat with sales</div>
                </div>
              </a>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-7"
          >
            <div className="rounded-3xl p-8 lg:p-10 bg-[#0B1224]/80 border border-white/10">
              {success ? (
                <div className="py-8 text-center" data-testid="contact-success">
                  <div className="mx-auto w-14 h-14 rounded-full bg-[#2563EB]/20 border border-[#2563EB]/40 flex items-center justify-center">
                    <CheckCircle2 className="w-7 h-7 text-[#00D4FF]"/>
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-bold">Message received</h3>
                  <p className="mt-2 text-gray-400 max-w-md mx-auto">Our team will reach out within 24 hours. Meanwhile, keep an eye on your inbox.</p>
                  <Button
                    onClick={() => setSuccess(false)}
                    className="mt-6 btn-glow bg-[#2563EB] hover:bg-[#1d4ed8] rounded-full h-11 px-6"
                    data-testid="contact-send-another"
                  >
                    Send another
                  </Button>
                </div>
              ) : (
                <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-form">
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-400">Full Name *</Label>
                    <Input required data-testid="contact-full-name" value={form.full_name} onChange={set("full_name")} className="bg-black/30 border-white/10 focus-visible:ring-[#2563EB]" placeholder="Jane Cooper"/>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-400">Company *</Label>
                    <Input required data-testid="contact-company" value={form.company} onChange={set("company")} className="bg-black/30 border-white/10 focus-visible:ring-[#2563EB]" placeholder="Acme Gaming"/>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-400">Email *</Label>
                    <Input required type="email" data-testid="contact-email" value={form.email} onChange={set("email")} className="bg-black/30 border-white/10 focus-visible:ring-[#2563EB]" placeholder="you@company.com"/>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-400">Phone *</Label>
                    <Input required data-testid="contact-phone" value={form.phone} onChange={set("phone")} className="bg-black/30 border-white/10 focus-visible:ring-[#2563EB]" placeholder="+44 7000 000000"/>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-400">Country *</Label>
                    <Select value={form.country} onValueChange={(v)=>setForm(f=>({...f, country:v}))}>
                      <SelectTrigger data-testid="contact-country" className="bg-black/30 border-white/10 text-white">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0B1224] border-white/10 text-white max-h-64">
                        {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-gray-400">Interested In</Label>
                    <Select value={form.service_interest} onValueChange={(v)=>setForm(f=>({...f, service_interest:v}))}>
                      <SelectTrigger data-testid="contact-service" className="bg-black/30 border-white/10 text-white">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0B1224] border-white/10 text-white">
                        {SERVICE_INTERESTS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label className="text-xs text-gray-400">Message</Label>
                    <Textarea data-testid="contact-message" value={form.message} onChange={set("message")} rows={5} className="bg-black/30 border-white/10 focus-visible:ring-[#2563EB]" placeholder="Tell us about your project, target markets, timeline..."/>
                  </div>
                  <div className="md:col-span-2 flex justify-between items-center pt-2">
                    <p className="text-xs text-gray-500">By submitting, you agree to our terms.</p>
                    <Button
                      type="submit"
                      disabled={submitting || !form.country}
                      data-testid="contact-submit"
                      className="btn-glow bg-[#2563EB] hover:bg-[#1d4ed8] rounded-full h-11 px-7"
                    >
                      {submitting ? "Sending..." : (<>Send Message <Send className="ml-2 w-4 h-4"/></>)}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
